import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { ItemProps } from '../interfaces';
import { isLinkItem } from '../utils/utils';
import LinkItem from './link-item';
import ItemIcon from './item-icon';
import styles from './styles.css.js';

const ItemElement = ({
  item,
  disabled,
  onItemActivate,
  highlighted,
  highlightItem,
  first = false,
  last,
  hasCategoryHeader,
  variant = 'normal',
}: ItemProps) => {
  const isLink = isLinkItem(item);

  const onClick = (event: React.MouseEvent) => {
    event.preventDefault();
    if (!disabled) {
      onItemActivate(item, event);
    }
  };

  const onLinkClick = (event: React.MouseEvent) => {
    if (!disabled) {
      onItemActivate(item, event);
    }
  };

  const onHover = (event: React.SyntheticEvent) => {
    event.preventDefault();
    !disabled && highlightItem(item);
  };

  const itemRef = useRef<HTMLSpanElement & HTMLAnchorElement>(null);

  useEffect(() => {
    if (highlighted && itemRef.current) {
      itemRef.current.focus();
    }
  }, [highlighted]);

  const commonProps: React.HTMLProps<HTMLSpanElement & HTMLAnchorElement> = {
    ref: itemRef,
    className: styles['item-element-inner'],
    role: 'menuitem',
    'aria-disabled': disabled ? 'true' : undefined,
    // We are using the roving tabindex technique to manage the focus state of the dropdown.
    // The current element will always have tabindex=0 which means that it can be tabbed to,
    // while all other items have tabindex=-1 so we can focus them when necessary.
    tabIndex: disabled ? undefined : highlighted ? 0 : -1,
  };

  const hasIcon = !!(item.iconName || item.iconUrl || item.iconSvg);

  return (
    <li
      className={clsx(styles['item-element'], styles[`variant-${variant}`], {
        [styles.highlighted]: highlighted,
        [styles.disabled]: disabled,
        [styles.first]: first,
        [styles.last]: last,
        [styles['has-category-header']]: hasCategoryHeader,
        [styles['item-link']]: isLink,
      })}
      role="presentation"
      data-testid={item.id}
      data-description={item.description}
      onClick={isLink ? onLinkClick : onClick}
      onMouseEnter={!disabled ? onHover : undefined}
      onTouchStart={!disabled ? onHover : undefined}
    >
      {isLink ? (
        <LinkItem item={item} disabled={disabled} optionProps={commonProps} />
      ) : (
        <span {...commonProps}>
          {hasIcon && <ItemIcon name={item.iconName} url={item.iconUrl} svg={item.iconSvg} alt={item.iconAlt} />}{' '}
          {item.text}
        </span>
      )}
    </li>
  );
};

export default ItemElement;
